export interface Engineer {
  id: number;
  name: string;
  initials: string;
  avatarColor: string;
  role: string;
  skills: string[];
  seniority: 'Junior' | 'Mid' | 'Senior' | 'Lead' | 'Principal';
  years: number;
  timezone: string;
  timezoneOffset: string;
  status: 'Available Now' | 'Interviewing' | 'Matched This Week' | 'Returning Soon';
  rateMin: number;
  rateMax: number;
  languages: string[];
  industries: string[];
  highlight?: string;
}

export const engineers: Engineer[] = [
  {
    id: 1,
    name: 'Alex Chen',
    initials: 'AC',
    avatarColor: 'bg-emerald-500',
    role: 'Senior React Engineer',
    skills: ['React', 'TypeScript', 'Next.js', 'GraphQL'],
    seniority: 'Senior',
    years: 7,
    timezone: 'EST (New York)',
    timezoneOffset: 'UTC-5',
    status: 'Available Now',
    rateMin: 75,
    rateMax: 95,
    languages: ['English', 'Mandarin'],
    industries: ['FinTech', 'SaaS'],
    highlight: 'Shipped 3 production React apps in the last 12 months',
  },
  {
    id: 2,
    name: 'Maria Santos',
    initials: 'MS',
    avatarColor: 'bg-amber-500',
    role: 'Lead Backend Developer',
    skills: ['Node.js', 'Python', 'PostgreSQL', 'AWS'],
    seniority: 'Lead',
    years: 9,
    timezone: 'CET (Berlin)',
    timezoneOffset: 'UTC+1',
    status: 'Available Now',
    rateMin: 95,
    rateMax: 120,
    languages: ['English', 'Portuguese', 'German'],
    industries: ['E-commerce', 'Logistics'],
    highlight: 'Built microservices handling 2M+ requests/day',
  },
  {
    id: 3,
    name: 'James Okafor',
    initials: 'JO',
    avatarColor: 'bg-blue-500',
    role: 'Full-Stack Engineer',
    skills: ['React', 'Node.js', 'MongoDB', 'Docker'],
    seniority: 'Mid',
    years: 4,
    timezone: 'GMT (London)',
    timezoneOffset: 'UTC+0',
    status: 'Interviewing',
    rateMin: 55,
    rateMax: 70,
    languages: ['English'],
    industries: ['HealthTech', 'EdTech'],
  },
  {
    id: 4,
    name: 'Priya Sharma',
    initials: 'PS',
    avatarColor: 'bg-rose-500',
    role: 'DevOps / SRE',
    skills: ['Kubernetes', 'Terraform', 'Azure', 'CI/CD'],
    seniority: 'Senior',
    years: 6,
    timezone: 'IST (Bangalore)',
    timezoneOffset: 'UTC+5:30',
    status: 'Available Now',
    rateMin: 65,
    rateMax: 85,
    languages: ['English', 'Hindi'],
    industries: ['SaaS', 'Cloud'],
    highlight: 'Reduced deployment time by 73% at previous client',
  },
  {
    id: 5,
    name: 'David Kim',
    initials: 'DK',
    avatarColor: 'bg-indigo-500',
    role: 'Mobile Developer',
    skills: ['Swift', 'iOS', 'React Native', 'Firebase'],
    seniority: 'Senior',
    years: 6,
    timezone: 'PST (San Francisco)',
    timezoneOffset: 'UTC-8',
    status: 'Matched This Week',
    rateMin: 80,
    rateMax: 100,
    languages: ['English', 'Korean'],
    industries: ['Consumer', 'Mobile'],
  },
  {
    id: 6,
    name: 'Sofia Andersson',
    initials: 'SA',
    avatarColor: 'bg-teal-500',
    role: 'Data Engineer',
    skills: ['Python', 'Spark', 'Snowflake', 'Airflow'],
    seniority: 'Senior',
    years: 5,
    timezone: 'CET (Stockholm)',
    timezoneOffset: 'UTC+1',
    status: 'Available Now',
    rateMin: 70,
    rateMax: 90,
    languages: ['English', 'Swedish'],
    industries: ['Data', 'Analytics'],
  },
  {
    id: 7,
    name: 'Marcus Johnson',
    initials: 'MJ',
    avatarColor: 'bg-orange-500',
    role: 'Junior Frontend Developer',
    skills: ['React', 'CSS', 'Tailwind', 'JavaScript'],
    seniority: 'Junior',
    years: 1,
    timezone: 'CST (Chicago)',
    timezoneOffset: 'UTC-6',
    status: 'Available Now',
    rateMin: 35,
    rateMax: 45,
    languages: ['English'],
    industries: ['SaaS', 'Agency'],
    highlight: 'Top 5% graduate from Fullstack Academy bootcamp',
  },
  {
    id: 8,
    name: 'Lina Petrov',
    initials: 'LP',
    avatarColor: 'bg-violet-500',
    role: 'Principal Architect',
    skills: ['Java', 'Spring', 'Kafka', 'AWS'],
    seniority: 'Principal',
    years: 12,
    timezone: 'EET (Tallinn)',
    timezoneOffset: 'UTC+2',
    status: 'Returning Soon',
    rateMin: 130,
    rateMax: 160,
    languages: ['English', 'Russian', 'Estonian'],
    industries: ['FinTech', 'Enterprise'],
    highlight: 'Led architecture for a $2B payment platform',
  },
  {
    id: 9,
    name: 'Yuki Tanaka',
    initials: 'YT',
    avatarColor: 'bg-cyan-500',
    role: 'ML Engineer',
    skills: ['Python', 'PyTorch', 'TensorFlow', 'MLOps'],
    seniority: 'Senior',
    years: 5,
    timezone: 'JST (Tokyo)',
    timezoneOffset: 'UTC+9',
    status: 'Interviewing',
    rateMin: 85,
    rateMax: 105,
    languages: ['English', 'Japanese'],
    industries: ['AI/ML', 'HealthTech'],
  },
  {
    id: 10,
    name: 'Emily Brown',
    initials: 'EB',
    avatarColor: 'bg-pink-500',
    role: 'Mid-Level .NET Developer',
    skills: ['.NET', 'C#', 'Azure', 'SQL Server'],
    seniority: 'Mid',
    years: 3,
    timezone: 'AEST (Sydney)',
    timezoneOffset: 'UTC+10',
    status: 'Available Now',
    rateMin: 50,
    rateMax: 65,
    languages: ['English'],
    industries: ['Enterprise', 'Government'],
  },
  {
    id: 11,
    name: 'Ravi Patel',
    initials: 'RP',
    avatarColor: 'bg-lime-600',
    role: 'Vue.js Specialist',
    skills: ['Vue.js', 'Nuxt', 'JavaScript', 'Node.js'],
    seniority: 'Senior',
    years: 6,
    timezone: 'IST (Mumbai)',
    timezoneOffset: 'UTC+5:30',
    status: 'Matched This Week',
    rateMin: 60,
    rateMax: 80,
    languages: ['English', 'Hindi', 'Gujarati'],
    industries: ['E-commerce', 'SaaS'],
  },
  {
    id: 12,
    name: 'Olivia Müller',
    initials: 'OM',
    avatarColor: 'bg-sky-500',
    role: 'QA Automation Lead',
    skills: ['Cypress', 'Playwright', 'Selenium', 'CI/CD'],
    seniority: 'Lead',
    years: 8,
    timezone: 'CET (Munich)',
    timezoneOffset: 'UTC+1',
    status: 'Available Now',
    rateMin: 80,
    rateMax: 100,
    languages: ['English', 'German'],
    industries: ['SaaS', 'Automotive'],
    highlight: 'Cut regression test suite from 8 hours to 45 minutes',
  },
];

export const allSkills = [
  'React',
  'Node.js',
  'Python',
  'Java',
  '.NET',
  'Vue.js',
  'Swift',
  'TypeScript',
  'Next.js',
  'GraphQL',
  'AWS',
  'Azure',
  'Kubernetes',
  'Docker',
  'PostgreSQL',
  'MongoDB',
  'Terraform',
  'Spark',
  'Snowflake',
  'PyTorch',
  'TensorFlow',
  'Cypress',
  'Playwright',
  'CI/CD',
  'Kafka',
];

export const allSeniorities = ['Junior', 'Mid', 'Senior', 'Lead', 'Principal'];

export const allTimezones = [
  'EST (New York)',
  'CST (Chicago)',
  'MST (Denver)',
  'PST (San Francisco)',
  'GMT (London)',
  'CET (Berlin)',
  'EET (Tallinn)',
  'IST (Bangalore)',
  'IST (Mumbai)',
  'JST (Tokyo)',
  'AEST (Sydney)',
];

export const allStatuses = [
  'Available Now',
  'Interviewing',
  'Matched This Week',
  'Returning Soon',
];